// src/components/lesson/RenderSanityStep.jsx

import React from "react";
import LevelVideoTemplate from "./LevelVideoTemplate";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  getLevelScoreConfig,
  saveKnowledgeCheckCompletion,
} from "../../lib/learning/levelScoring";

import { secureKnowledgeChecksEnabled } from "../../lib/learning/secureKnowledgeChecks";
import WelcomeTemplate from "./WelcomeTemplate";
import IntroTemplate from "./IntroTemplate";
import KeyConceptTemplate from "./KeyConceptTemplate";
import ConceptExampleTemplate from "./ConceptExampleTemplate";
import ScenarioSelectTemplate from "./ScenarioSelectTemplate";
import ScenarioDragTemplate from "./ScenarioDragTemplate";
import KeyTakeawayTemplate from "./KeyTakeawayTemplate";
import PracticalChallengesTemplate from "./PracticalChallengeTemplate";
import LessonStepShell from "./LessonStepShell";

function cleanCanvasText(value, prefixes = []) {
  const text = String(value || "").trim();
  if (!text) return "";

  let cleaned = text;

  prefixes.forEach((prefix) => {
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const compact = prefix.replace(/\s+/g, "");
    const escapedCompact = compact.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    cleaned = cleaned
      .replace(new RegExp(`^${escaped}\\s*:?\\s*`, "i"), "")
      .replace(new RegExp(`^${escapedCompact}\\s*:?\\s*`, "i"), "");
  });

  return cleaned.trim();
}

function parsePairsText(pairsText = "") {
  return String(pairsText || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [drag = "", drop = "", matchKey = ""] = line
        .split("|")
        .map((part) => part.trim());

      const cleanDrag = cleanCanvasText(drag, ["Drag"]);
      const cleanDrop = cleanCanvasText(drop, ["Drop"]);
      const cleanKey = cleanCanvasText(matchKey, ["Match Key", "Key"]);

      const fallbackKey =
        cleanKey ||
        `${cleanDrag || "pair"}-${index + 1}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");

      return {
        _key: fallbackKey,
        id: fallbackKey,
        dragText: cleanDrag,
        dropText: cleanDrop,
        matchKey: fallbackKey,
      };
    });
}

function parseAnswersText(answersText = "") {
  return String(answersText || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [answerText = "", statusText = ""] = line
        .split("|")
        .map((part) => part.trim());

      const cleanAnswer = cleanCanvasText(answerText, ["Answer", "Label"]);

      const cleanStatus = cleanCanvasText(statusText, [
        "Answer Status",
        "Status",
        "Correct Answer",
        "Correct",
      ]);

      const isCorrect = cleanStatus.toLowerCase() === "correct";

      return {
        _key: `answer-${index + 1}`,
        id: index + 1,
        title: cleanAnswer,
        label: cleanAnswer,
        answerStatus: isCorrect ? "Correct" : "Incorrect",
        isCorrect,
        correct: isCorrect,
      };
    });
}

function getStepPairs(step) {
  if (step?.pairsText && String(step.pairsText).trim()) {
    return parsePairsText(step.pairsText);
  }

  if (Array.isArray(step?.pairs) && step.pairs.length > 0) {
    return step.pairs.map((pair, index) => {
      const dragText = cleanCanvasText(pair?.dragText || "", [
        "Pair",
        "Drag",
        "Drag Text",
      ]);

      const dropText = cleanCanvasText(pair?.dropText || "", [
        "Drop",
        "Drop Text",
      ]);

      const matchKey = cleanCanvasText(pair?.matchKey || "", [
        "Match Key",
        "Key",
      ]);

      const fallbackKey =
        matchKey ||
        pair?._key ||
        pair?.id ||
        `${dragText || "pair"}-${index + 1}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");

      return {
        ...pair,
        id: pair?.id || fallbackKey,
        dragText,
        dropText,
        matchKey: fallbackKey,
      };
    });
  }

  return [];
}

function getStepOptions(step) {
  if (step?.answersText && String(step.answersText).trim()) {
    return parseAnswersText(step.answersText);
  }

  return Array.isArray(step?.options) ? step.options : [];
}

function getPairKey(pair, index) {
  return (
    pair?.matchKey ||
    pair?._key ||
    pair?.id ||
    `${String(pair?.dragText || "drag")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")}-${index + 1}`
  );
}

function buildWordsFromStep(step) {
  const pairs = getStepPairs(step);

  if (pairs.length > 0) {
    return pairs.map((pair, index) => {
      const matchKey = getPairKey(pair, index);

      return {
        /*
         * Each draggable item needs its own unique physical ID.
         * Multiple items are allowed to share the same matchKey.
         */
        id: `word-${matchKey}-${index + 1}`,
        text: cleanCanvasText(pair?.dragText || "", ["Drag"]),
        matchKey,
      };
    });
  }

  return (step?.items || []).map((item, index) => {
    const matchKey = item.matchKey || `item-${index + 1}`;

    return {
      id: `word-${item.id || item._key || matchKey}-${index + 1}`,
      answerKey: item._key || item.id,
      text: cleanCanvasText(item.text || "", ["Drag", "Answer"]),
      matchKey,
    };
  });
}

function buildDefinitionsFromStep(step) {
  const pairs = getStepPairs(step);

  if (pairs.length > 0) {
    return pairs.map((pair, index) => {
      const matchKey = getPairKey(pair, index);

      return {
        id: `definition-${matchKey}-${index + 1}`,
        text: cleanCanvasText(pair?.dropText || "", ["Drop"]),
        matchKey,
      };
    });
  }

  return (step?.targets || []).map((target, index) => {
    const matchKey = target.matchKey || `target-${index + 1}`;

    return {
      id: `definition-${target.id || target._key || matchKey}-${index + 1}`,
      boxKey: target.boxKey || target.id,
      text: cleanCanvasText(target.text || "", ["Drop", "Definition"]),
      matchKey,
      icon: target.icon?.asset?.url,
    };
  });
}

function normalizeAnswerOptions(options = []) {
  return options.map((option, index) => {
    const isCorrect =
      option.answerStatus === "Correct" ||
      option.isCorrect === true ||
      option.isCorrect === "true" ||
      option.correct === true ||
      option.correct === "true";

    const title = cleanCanvasText(
      option.title || option.label || option.text || "",
      ["Answer", "Label"]
    );

    return {
      ...option,
      id: option.id || option._key || index + 1,
      title,
      label: title,
      answerStatus: isCorrect ? "Correct" : "Incorrect",
      isCorrect,
      correct: isCorrect,
    };
  });
}

function cleanStepName(value) {
  return cleanCanvasText(value, ["Page Name", "Name", "CMS Name"]);
}

function cleanTitle(value) {
  return cleanCanvasText(value, ["Title", "Display Title"]);
}

function cleanQuestion(value) {
  return cleanCanvasText(value, ["Question", "Prompt"]);
}

function isSummaryVideoStep(step) {
  const videoType = String(step?.videoType || "").trim().toLowerCase();

  const rawTitle = cleanTitle(
    step?.title ||
      step?.displayTitle ||
      ""
  );

  const titleLower = rawTitle.toLowerCase();

  return (
    videoType === "summary" ||
    titleLower.includes("summary")
  );
}

function ResultRedirect({ to, nextTo, storageKey, completedStorageKey }) {
  React.useEffect(() => {
    try {
      if (completedStorageKey) {
        sessionStorage.setItem(completedStorageKey, "true");
      }

      if (storageKey) {
        if (nextTo) {
          sessionStorage.setItem(storageKey, nextTo);
        } else {
          sessionStorage.removeItem(storageKey);
        }
      }
    } catch {}
  }, [nextTo, storageKey, completedStorageKey]);

  return <Navigate replace to={to} />;
}

export default function RenderSanityStep({
  step,
  hasPracticalAction = false,
  actionQuestionCount = 0,
  nextTo,
  prevTo,
  currentSegment,

  conceptTitle = "",
  conceptNumber = 1,
  conceptTotal = 1,

  levelTitle = "",

  progressStep = 1,
  progressTotal = 20,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const { questId, subquestId, levelKey } = useParams();

  const levelScoreConfig = getLevelScoreConfig(levelKey);
  const levelHasActionPage = hasPracticalAction;

  const defaultActionTaskKey = `${levelScoreConfig.levelKey.toLowerCase()}-application-task`;

  /*
   * When a level is not allowed to have an action page,
   * any accidental Sanity action page redirects straight to result.
   */
  const skipActionPageTo = `/play/${questId}/${subquestId}/${levelKey}/result`;

  if (!step) return null;

  const cleanedInternalTitle = cleanStepName(step.internalTitle);

  // Only redesigned knowledge checks opt into secure scoring; legacy lessons remain unchanged.
  const secureAssessment = secureKnowledgeChecksEnabled &&
    Number.isInteger(Number(step.knowledgeCheckNumber)) &&
    Number(step.knowledgeCheckNumber) > 0
      ? {
          levelKey,
          questionKey: `KC${step.knowledgeCheckNumber}`,
          curriculumGeneration: step.curriculumGeneration,
assessmentVersion: Number(step.assessmentVersion),
        }
      : null;

  const stepKey =
    step._key ||
    cleanedInternalTitle ||
    step.displayTitle ||
    step.heading ||
    currentSegment;

  const sharedConceptTitle =
    cleanTitle(conceptTitle) || cleanTitle(step.displayTitle) || "";

  const sharedProgressProps = {
    progressStep,
    progressTotal,
  };

  const sharedConceptProps = {
    title: sharedConceptTitle,
    conceptNumber,
    conceptTotal,
    conceptIcon: step.conceptIcon || "💡",
    ...sharedProgressProps,
  };

  const resultNextStorageKey = `result_next_${questId}_${subquestId}_${levelKey}`;
  const resultCompletedStorageKey = `sanity_result_completed_${questId}_${subquestId}_${levelKey}`;

  const currentSearchParams = new URLSearchParams(location.search);
  currentSearchParams.delete("retryTo");

  const currentSearch = currentSearchParams.toString();

  const currentStepUrl = `${location.pathname}${
    currentSearch ? `?${currentSearch}` : ""
  }`;

  /*
   * When a learner gets a Knowledge Check wrong:
   * Incorrect page → Related video → Same Knowledge Check.
   *
   * We store the current Knowledge Check URL as retryTo on the video URL.
   * The video step then uses retryTo as its next destination.
   */
  const isVideoRoute = (url = "") => {
  const cleanedUrl = String(url || "").toLowerCase();

  return (
    cleanedUrl.includes("video") ||
    cleanedUrl.includes("intro") ||
    cleanedUrl.includes("summary")
  );
};

const goToRetryVideo = () => {
  /*
   * Incorrect page → Review
   *
   * If the next route is a support/retry video:
   * go to that video and store the current question as retryTo.
   *
   * If there is no video route:
   * go back to the same Knowledge Check question.
   */
  if (nextTo && isVideoRoute(nextTo)) {
    const separator = nextTo.includes("?") ? "&" : "?";

    navigate(
      `${nextTo}${separator}retryTo=${encodeURIComponent(currentStepUrl)}`
    );

    return;
  }

  navigate(currentStepUrl, {
    replace: true,
  });
};

  const retryTo = new URLSearchParams(location.search).get("retryTo") || "";

  const goToVideoNext = () => {
    if (retryTo) {
      navigate(retryTo, {
        replace: true,
      });
      return;
    }

    if (nextTo) {
      navigate(nextTo);
    }
  };

  const withLessonShell = (content) => (
    <LessonStepShell>{content}</LessonStepShell>
  );

  switch (step._type) {
    case "overviewDraftStep":
    case "welcomeStep":
      return withLessonShell(
        <WelcomeTemplate
          key={stepKey}
          levelHeading={cleanCanvasText(step.levelHeading || step.heading, [
            "Heading",
            "Level Heading",
          ])}
          levelOverviewText={cleanCanvasText(
            step.levelOverviewText || step.introText || "",
            ["Overview", "Level Overview Text", "Intro"]
          )}
          estimatedTime={step.estimatedTime || ""}
          xpLabel={step.xpLabel || ""}
          overviewImageSrc={step.overviewImageSrc || ""}
          heading={cleanCanvasText(step.heading, ["Heading"])}
          introText={cleanCanvasText(step.introText || "", ["Intro"])}
          learningOutcomes={step.learningOutcomes || []}
          startLabel={step.startLabel || "START"}
          startTo={nextTo || "#"}
          backTo={prevTo}
          {...sharedProgressProps}
        />
      );

    case "introductionStep":
      return withLessonShell(
        <IntroTemplate
          key={stepKey}
          levelName={cleanedInternalTitle || levelTitle}
          paragraphs={step.paragraphs || []}
          imageSrc={step.illustration?.asset?.url}
          imageAlt={step.imageAlt}
          nextTo={nextTo}
          backTo={prevTo}
          nextLabel={step.nextLabel || "NEXT"}
          {...sharedProgressProps}
        />
      );

    case "keyConceptStep":
      return withLessonShell(
        <KeyConceptTemplate
          key={stepKey}
          {...sharedConceptProps}
          title={cleanTitle(step.displayTitle || sharedConceptTitle)}
          paragraphs={step.paragraphs || []}
          imageSrc={step.illustration?.asset?.url}
          imageAlt={step.imageAlt}
          nextTo={nextTo}
          backTo={prevTo}
          nextLabel={step.nextLabel || "NEXT"}
        />
      );

    case "videoStep": {
      const showSummaryTitle = isSummaryVideoStep(step);

      return withLessonShell(
        <LevelVideoTemplate
          key={stepKey}
          title={showSummaryTitle ? "Summary" : ""}
          showHeading={showSummaryTitle}
          videoUrl={step.videoUrl || ""}
          videoType={step.videoType || "video"}
          knowledgeCheckNumber={step.knowledgeCheckNumber || null}
          nextTo={retryTo || nextTo}
          backTo={retryTo || prevTo}
          nextLabel={step.nextLabel || "NEXT"}
          onNext={goToVideoNext}
          {...sharedProgressProps}
        />
      );
    }

    case "conceptExampleStep":
      return withLessonShell(
        <ConceptExampleTemplate
          key={stepKey}
          {...sharedConceptProps}
          title={cleanTitle(step.displayTitle || sharedConceptTitle)}
          storyParagraphs={step.storyParagraphs || []}
          imageSrc={step.illustration?.asset?.url}
          imageAlt={step.imageAlt}
          nextTo={nextTo}
          backTo={prevTo}
          nextLabel={step.nextLabel || "NEXT"}
        />
      );

    /*
     * No quizSelectStep / quizDragStep cases.
     * Those legacy Sanity steps are filtered out by SanityFlowRunner
     * before they reach this renderer.
     */

    case "scenarioSelectStep":
      return withLessonShell(
        <ScenarioSelectTemplate
          secureAssessment={secureAssessment}
          key={stepKey}
          questionKey={step._key || stepKey}
          {...sharedConceptProps}
          title={cleanTitle(step.displayTitle || sharedConceptTitle || "")}
          correctFeedbackText={step.correctFeedbackText || ""}
          incorrectFeedbackText={step.incorrectFeedbackText || ""}
          incorrectSupportText={step.incorrectSupportText || ""}
          scenarioText={step.scenarioText || step.scenarioParagraphOne || ""}
          characterImageSrc={
            step.characterImageSrc ||
            step.characterImage?.asset?.url ||
            step.contextImage?.asset?.url ||
            ""
          }
          characterImageAlt={step.characterImageAlt || ""}
          prompt={cleanQuestion(step.prompt || "")}
          options={normalizeAnswerOptions(getStepOptions(step))}
          ctaNextLabel={step.ctaNextLabel || "NEXT"}
          showTitle={step.showTitle !== false}
          backTo={prevTo}
          onNext={() => {
            if (!secureAssessment) {
              saveKnowledgeCheckCompletion({
                levelKey,
                questionKey: step._key || stepKey,
                scoreValue: 1,
              });
            }

            if (nextTo) {
              navigate(nextTo);
            }
          }}
          onIncorrectRetry={goToRetryVideo}
        />
      );

    case "scenarioDragStep":
      return withLessonShell(
        <ScenarioDragTemplate
          secureAssessment={secureAssessment}
          key={stepKey}
          questionKey={step._key || stepKey}
          {...sharedConceptProps}
          title={cleanTitle(step.displayTitle || sharedConceptTitle || "")}
          correctFeedbackText={step.correctFeedbackText || ""}
          incorrectFeedbackText={step.incorrectFeedbackText || ""}
          incorrectSupportText={step.incorrectSupportText || ""}
          scenarioText={step.scenarioText || step.scenarioParagraphOne || ""}
          characterImageSrc={
            step.characterImageSrc ||
            step.characterImage?.asset?.url ||
            step.contextImage?.asset?.url ||
            ""
          }
          characterImageAlt={step.characterImageAlt || ""}
          prompt={cleanQuestion(step.prompt || "")}
          words={buildWordsFromStep(step)}
          definitions={buildDefinitionsFromStep(step)}
          validatePair={(word, definition) =>
            word?.matchKey === definition?.matchKey
          }
          nextLabel={step.nextLabel || "NEXT"}
          helperMain={step.helperMain}
          helperTip={step.helperTip}
          showTitle={step.showTitle !== false}
          backTo={prevTo}
          onNext={() => {
            if (!secureAssessment) {
              saveKnowledgeCheckCompletion({
                levelKey,
                questionKey: step._key || stepKey,
                scoreValue: 1,
              });
            }

            if (nextTo) {
              navigate(nextTo);
            }
          }}
          onIncorrectRetry={goToRetryVideo}
        />
      );

    case "keyTakeawayStep":
      return withLessonShell(
        <KeyTakeawayTemplate
          key={stepKey}
          levelName={cleanedInternalTitle || levelTitle}
          speechText={cleanCanvasText(step.speechText, ["Summary"])}
          /*
           * Force NEXT during the transition.
           * Existing Sanity may still contain TAKE QUIZ.
           */
          nextLabel="NEXT"
          backTo={prevTo}
          onNext={() => {
            if (nextTo) {
              navigate(nextTo);
            }
          }}
          {...sharedProgressProps}
        />
      );

    case "practicalActionDraftStep":
      if (!levelHasActionPage) {
        return <Navigate replace to={skipActionPageTo} />;
      }

      return withLessonShell(
        <PracticalChallengesTemplate
          key={stepKey}
          variant="action"
          levelKey={levelKey}
          taskKey={step.taskKey || defaultActionTaskKey}
          taskMode={step.taskMode || "application_task"}
          title={cleanTitle(
            step.displayTitle || step.actionTitle || "Practical Action"
          )}
          actionLabel="Your practical action"
          introText={
            step.shortExplanation ||
            step.introText ||
            "Let’s turn this lesson into one simple business action."
          }
          content={null}
          nextLabel={step.nextLabel || "NEXT"}
          backTo={prevTo}
          onNext={() => {
            if (nextTo) {
              navigate(nextTo);
            }
          }}
          {...sharedProgressProps}
        />
      );

    case "actionQuestionDraftStep":
      if (!levelHasActionPage) {
        return <Navigate replace to={skipActionPageTo} />;
      }

      return withLessonShell(
        <PracticalChallengesTemplate
          key={stepKey}
          variant="question"
          levelKey={levelKey}
          // Only the authored Sanity _key is accepted for verified submissions.
          // Never fall back to a number/route, as it would not match Supabase.
          questionKey={step._key || ""}
          requireVerifiedAction
          taskKey={`${defaultActionTaskKey}-question-${step.actionQuestionNumber || step._key || currentSegment}`}
          taskMode={step.taskMode || "application_task"}
          title={cleanTitle(step.displayTitle || "Action Question")}
          actionLabel="Action question"
          introText={step.shortExplanation || ""}
          question={step.questionText || ""}
          questionSupport="Choose the option that is most relevant to your business."
          options={step.options || []}
          nextLabel={step.nextLabel || "NEXT"}
          backTo={prevTo}
          onActionSubmit={(_payload, verification) => {
            // The RPC is awaited by PracticalChallengesTemplate before navigation.
            // Server-reported A-XP is authoritative; local task XP is not.
            if (verification?.completed) {
              console.info("[Gwin] Practical Action complete:", {
                levelKey,
                answered: verification.answered_questions,
                required: verification.required_questions,
                newlyAwarded: verification.newly_awarded,
              });
            }
          }}
          onNext={() => {
            if (nextTo) {
              navigate(nextTo);
            }
          }}
          {...sharedProgressProps}
        />
      );

    case "practicalActionCompleteDraftStep":
      if (!levelHasActionPage) {
        return <Navigate replace to={skipActionPageTo} />;
      }

      return withLessonShell(
        <PracticalChallengesTemplate
          key={stepKey}
          variant="complete"
          levelKey={levelKey}
          taskKey={step.taskKey || defaultActionTaskKey}
          taskMode={step.taskMode || "application_task"}
          title={cleanTitle(
            step.displayTitle || step.heading || "Practical Action Complete"
          )}
          actionLabel="Action complete"
          completionHeading={step.heading || "Practical Action Completed!"}
          completionText={
            [step.textOne, step.textTwo].filter(Boolean).join("\n\n") ||
            "You now have one clear place to start."
          }
          nextLabel={step.nextLabel || "NEXT"}
          backTo={prevTo}
          onNext={() => {
            if (nextTo) {
              navigate(nextTo);
            }
          }}
          {...sharedProgressProps}
        />
      );

    case "practicalStep":
      if (!levelHasActionPage) {
        return <Navigate replace to={skipActionPageTo} />;
      }

      return withLessonShell(
        <PracticalChallengesTemplate
          key={stepKey}
          variant="legacy"
          levelKey={levelKey}
          taskKey={step.taskKey || defaultActionTaskKey}
          taskMode={step.taskMode || "application_task"}
          title={cleanTitle(step.displayTitle || "")}
          content={
            <div className="whitespace-pre-wrap">
              {cleanCanvasText(step.content, ["Task"])}
            </div>
          }
          actionLabel="Your practical action"
          introText={step.introText || "Short explanation"}
          question={
            step.question ||
            "Which money area must you check first in your business?"
          }
          questionSupport={
            step.questionSupport ||
            "Choose the option that is most relevant to your business."
          }
          completionHeading={
            step.completionHeading || "Practical Action Completed!"
          }
          completionText={
            step.completionText || "You now have one clear place to start."
          }
          nextLabel={step.nextLabel || "NEXT"}
          backTo={prevTo}
          onNext={() => {
            if (nextTo) {
              navigate(nextTo);
            } else {
              navigate("/dashboard");
            }
          }}
          {...sharedProgressProps}
        />
      );

    case "resultStep":
      return (
        <ResultRedirect
          to={`/play/${questId}/${subquestId}/${levelKey}/result`}
          nextTo={nextTo}
          storageKey={resultNextStorageKey}
          completedStorageKey={resultCompletedStorageKey}
        />
      );

    default:
      return withLessonShell(
        <div className="p-4">
          Unsupported step type: {step._type} ({currentSegment})
        </div>
      );
  }
}